import React, { useState } from "react";
import { Button } from "../components/Button.jsx";
import { usePlayer, useStage, useRound, useGame } from "@empirica/core/player/classic/react";
import { getTaskConfig } from "../utils/taskConfig.js";
import { recordSubmittedIntegrityText } from "../utils/integrityTracking.js";
import { NO_SPINNER_CLASS, typedNumberInputProps } from "../utils/numberInput.js";

export function Choice() {
  const player = usePlayer();
  const stage = useStage();
  const round = useRound();
  const game = useGame();
  const treatment = game?.get("treatment") || {};
  const taskConfig = getTaskConfig(treatment);
  const options = taskConfig.options || [];
  const DetailsComponent = taskConfig.DetailsComponent;
  const factors = treatment.factors || {};
  const collectRationaleSetting = (
    typeof treatment.collectRationale === "boolean"
      ? treatment.collectRationale
      : (typeof factors.collectRationale === "boolean" ? factors.collectRationale : undefined)
  );
  const treatmentName = String(
    treatment.name ||
      treatment.treatmentName ||
      factors.name ||
      factors.treatmentName ||
      ""
  ).toLowerCase();
  const treatmentFingerprint = (
    typeof treatment === "string"
      ? treatment
      : JSON.stringify(treatment || {})
  ).toLowerCase();
  const isPilotNoGroupMeta =
    treatmentName.includes("pilot-nogroup-meta") ||
    treatmentName.includes("story-retire-pilot-nogroup-meta") ||
    treatmentName.includes("story-retire-pilot-nogroup-devil") ||
    treatmentName.includes("story-retire-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-pilot-nogroup-devil") ||
    treatmentName.includes("story-tax-pilot-nogroup-template") ||
    treatmentName.includes("story-tax-math-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-nogroup-template") ||
    treatmentName.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    treatmentName.includes("story-tax-math-openend-nogroup-template") ||
    treatmentName.includes("story-baserate-socratic-nogroup-norationale") ||
    treatmentName.includes("story-baserate-nogroup-template") ||
    treatmentFingerprint.includes("pilot-nogroup-meta") ||
    treatmentFingerprint.includes("story-retire-pilot-nogroup-meta") ||
    treatmentFingerprint.includes("story-retire-pilot-nogroup-devil") ||
    treatmentFingerprint.includes("story-retire-pilot-nogroup-template") ||
    treatmentFingerprint.includes("story-tax-pilot-nogroup-devil") ||
    treatmentFingerprint.includes("story-tax-pilot-nogroup-template") ||
    treatmentFingerprint.includes("story-tax-math-socratic-nogroup-norationale") ||
    treatmentFingerprint.includes("story-tax-math-nogroup-template") ||
    treatmentFingerprint.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    treatmentFingerprint.includes("story-tax-math-openend-nogroup-template") ||
    treatmentFingerprint.includes("story-baserate-socratic-nogroup-norationale") ||
    treatmentFingerprint.includes("story-baserate-nogroup-template");
  const shouldCollectRationale = typeof collectRationaleSetting === "boolean"
    ? collectRationaleSetting
    : !isPilotNoGroupMeta;
  const [selectedPrediction, setSelectedPrediction] = useState(() => {
    return player.round.get("initialBelief") || null;
  });
  const [explanation, setExplanation] = useState(() => {
    return player.round.get("initialBeliefExplanation") || "";
  });
  const [confidence, setConfidence] = useState(() => {
    const existing = player.round.get("initialBeliefConfidence");
    return existing === undefined || existing === null ? "" : String(existing);
  });
  const hasValidRationale = !shouldCollectRationale || explanation.trim().length > 0;
  const parsedConfidence = confidence === "" ? null : Number(confidence);
  const hasValidConfidence =
    parsedConfidence !== null &&
    Number.isFinite(parsedConfidence) &&
    parsedConfidence >= 0 &&
    parsedConfidence <= 100;

  function onSelect(choice) {
    // Only update the selected state, don't submit
    setSelectedPrediction(choice);
  }

  function onSubmit() {
    if (!selectedPrediction) {
      alert("Please select your prediction first.");
      return;
    }
    if (shouldCollectRationale && !hasValidRationale) {
      alert("Please provide your rationale before continuing.");
      return;
    }
    if (!hasValidConfidence) {
      alert("Please enter a confidence between 0 and 100.");
      return;
    }

    const timestamp = Date.now();
    const trimmedExplanation = explanation.trim();

    // Store directly on round (for backward compatibility)
    player.round.set("initialBelief", selectedPrediction);
    player.round.set("initialBeliefAt", timestamp);
    player.round.set("initialBeliefExplanation", trimmedExplanation);
    player.round.set("initialBeliefConfidence", parsedConfidence);
    player.round.set("initialBeliefConfidenceAt", timestamp);
    if (trimmedExplanation) {
      recordSubmittedIntegrityText({
        playerId: player.id,
        fieldId: "choice-rationale",
        text: trimmedExplanation,
        phase: "in-game",
        stageId: stage?.id,
        stageName: stage?.get("name"),
        detectedAt: timestamp,
      });
    }

    // Also store in playerBeliefs object (consistent with pre-lobby data)
    const existingBeliefs = round.get("playerBeliefs") || {};
    existingBeliefs[player.id] = {
      initialBelief: selectedPrediction,
      initialBeliefAt: timestamp,
      initialBeliefExplanation: trimmedExplanation,
      initialBeliefConfidence: parsedConfidence,
      initialBeliefConfidenceAt: timestamp
    };
    round.set("playerBeliefs", existingBeliefs);

    player.stage.set("submit", true);
    try {
      const events = round.get("eventLog") || [];
      events.push({
        t: timestamp,
        type: "click",
        stageId: stage?.id,
        stageName: stage?.get("name"),
        playerId: player.id,
        action: "initialBelief",
        value: selectedPrediction,
        explanation: trimmedExplanation,
        confidence: parsedConfidence
      });
      round.set("eventLog", events);
    } catch (_) {}
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">{taskConfig.title}</h2>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <p>{taskConfig.objectiveText}</p>

        {taskConfig.introPrompt && (
          <p className="text-lg mb-4">{taskConfig.introPrompt}</p>
        )}

        {taskConfig.caseNotice && (
          <p className="text-lg mb-4">
            <strong>{taskConfig.caseNotice}</strong>
          </p>
        )}

        {DetailsComponent ? <DetailsComponent /> : null}

      </div>

      <p className="text-xl mb-6">{taskConfig.questionText || "What is your belief?"}</p>

      <div className="flex justify-center gap-4 mb-6">
        {options.map((option) => {
          const label = option.buttonLabel || option.label || option.value;
          const isSelected = selectedPrediction === option.value;
          return (
            <Button
              key={option.value}
              className={`px-8 py-4 ${isSelected ? "bg-blue-700" : "bg-blue-500 hover:bg-blue-600"}`}
              handleClick={() => onSelect(option.value)}
            >
              {label}
            </Button>
          );
        })}
      </div>

      {selectedPrediction && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-center">
          <p className="text-lg">
            {(() => {
              const selectedOption = options.find((option) => option.value === selectedPrediction);
              const selectedLabel = selectedOption?.label || selectedPrediction;
              return (
                <>
                  You selected: <strong className="text-blue-700">{selectedLabel}</strong>
                </>
              );
            })()}
          </p>
        </div>
      )}

      {shouldCollectRationale && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <label className="block text-lg font-semibold mb-2">
            Please explain your rationale in depth
          </label>
          <textarea
            className="w-full border border-gray-300 rounded-md p-3 min-h-[120px]"
            data-integrity-field="choice-rationale"
            placeholder="Write a detailed explanation of why you chose this prediction."
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
          />
          {!explanation.trim() && (
            <p className="text-sm text-gray-500 mt-2">
              Please provide your rationale before continuing.
            </p>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <label className="block text-lg font-semibold mb-2">
          How confident are you in this prediction? (0-100%)
        </label>
        <input
          className={`w-full border border-gray-300 rounded-md p-3 ${NO_SPINNER_CLASS}`}
          type="number"
          data-integrity-field="choice-confidence"
          min="0"
          max="100"
          step="1"
          placeholder="0-100"
          value={confidence}
          onChange={(e) => setConfidence(e.target.value)}
          {...typedNumberInputProps()}
        />
        {!hasValidConfidence && (
          <p className="text-sm text-gray-500 mt-2">
            Please enter a confidence between 0 and 100.
          </p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center">
          <Button
            handleClick={onSubmit}
            className="px-8 py-3 bg-green-500 hover:bg-green-600"
            disabled={!selectedPrediction || !hasValidRationale || !hasValidConfidence}
          >
            Continue
          </Button>
        </div>
        {(!selectedPrediction || !hasValidRationale || !hasValidConfidence) && (
          <p className="text-sm text-gray-500 mt-2 text-center">
            {shouldCollectRationale
              ? "Please select your prediction, provide a rationale, and enter your confidence before continuing."
              : "Please select your prediction and enter your confidence before continuing."}
          </p>
        )}
      </div>
    </div>
  );
}
